import React from 'react';
import { LocationRangeOutDTO, NotificationAdditionalInfoOutDTO } from '@api/ringil-api.v2';
import { dayjsFromLocalDateString, formatDateFromDayjsWithLocale, stringHHmmFromStringInTz, stringYMDFromStringInTz } from '@utils/dateWithTz';
import { isNotNullOrUndefined } from '@utils/utils';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import BuildingSvg from '@icons/BuildingSvg.tsx';
import LocationTargetSvg from '@icons/LocationTargetSvg.tsx';
import CalendarSvg from '@icons/calendar.svg?react';
import ClockSvg from '@icons/ClockSvg.tsx';

/** `dateFrom`/`dateTo` are calendar days (YYYY-MM-DD) — formatted as-is, never converted to another timezone. */
const formatDayRange = (dateFrom?: string, dateTo?: string): string => {
  const from = formatDateFromDayjsWithLocale(dayjsFromLocalDateString(dateFrom));
  const to = formatDateFromDayjsWithLocale(dayjsFromLocalDateString(dateTo));
  if (!from && !to) return null;
  if (!to || from === to) return from;
  if (!from) return to;
  return `${from} — ${to}`;
};

/** One location of an expedition route — mirrors the "Trasa" column of the expedition list. */
const RouteLocation: React.FC<{ location: LocationRangeOutDTO; spaced?: boolean }> = ({ location, spaced }) => {
  if (!location) return null;
  const range = formatDayRange(location.dateFrom, location.dateTo);
  return (
    <>
      <div style={spaced ? { marginTop: '0.5rem' } : undefined}>{location.name}</div>
      {range && <div className="text-[#999] text-[smaller]">{range}</div>}
      <COTimezone timezoneId={location.timezoneId} />
    </>
  );
};

/** Loading / unloading location of a supplier notification — mirrors the SN list's "Loading/Unloading" column. */
const SupplierNotificationLocation: React.FC<{ location: LocationRangeOutDTO }> = ({ location }) => {
  if (!location) return null;
  const range = formatDayRange(location.dateFrom, location.dateTo);
  return (
    <div className="flex flex-col">
      <div className="text-[#616161]">{location.name}</div>
      {range && (
        <div className="text-[#616161] flex items-center gap-2">
          <CalendarSvg className={'text-content-secondary'} /> {range}
        </div>
      )}
      <COTimezone timezoneId={location.timezoneId} />
    </div>
  );
};

interface ComponentProps {
  additionalInfo?: NotificationAdditionalInfoOutDTO;
}

/**
 * Renders the "detail" column of a notification row. The backend fills exactly one of the
 * additionalInfo branches based on the entity the notification was raised for, and each branch is
 * rendered the way that entity's own list renders it (expedition → route, reservation → ramp and
 * time, supplier notification → loading/unloading). Older notifications carry none and render nothing.
 */
const NotificationAdditionalInfo: React.FC<ComponentProps> = ({ additionalInfo }) => {
  const { tr } = useTranslationLgs();

  if (!additionalInfo) return null;

  const { expedition, timeslot, supplierNotification } = additionalInfo;

  if (isNotNullOrUndefined(expedition)) {
    return (
      <div className="flex flex-col items-start">
        <RouteLocation location={expedition.firstLocation} />
        {expedition.intermediateStopsCount > 0 && (
          <div className="text-[#999] text-[smaller]">
            {tr('NotificationCenter.intermediateStops', `+{{count}} stop`, { count: expedition.intermediateStopsCount }, expedition.intermediateStopsCount)}
          </div>
        )}
        <RouteLocation location={expedition.lastLocation} spaced />
      </div>
    );
  }

  if (isNotNullOrUndefined(timeslot)) {
    // Prefer the timeslot's own instant + timezone; fall back to the plain calendar day when the
    // notification carries no start/end (unscheduled or pre-migration reservations).
    const hasInterval = isNotNullOrUndefined(timeslot.utcStart) && isNotNullOrUndefined(timeslot.utcEnd) && isNotNullOrUndefined(timeslot.timezoneId);
    const date = hasInterval ? stringYMDFromStringInTz(timeslot.utcStart, timeslot.timezoneId) : formatDateFromDayjsWithLocale(dayjsFromLocalDateString(timeslot.date));
    const since = hasInterval ? stringHHmmFromStringInTz(timeslot.utcStart, timeslot.timezoneId) : null;
    const till = hasInterval ? stringHHmmFromStringInTz(timeslot.utcEnd, timeslot.timezoneId) : null;

    return (
      <div className="flex flex-col">
        {timeslot.locationName && (
          <div className="font-medium flex items-center gap-2">
            <BuildingSvg />
            {timeslot.locationName}
          </div>
        )}
        {timeslot.rampName && (
          <div className="font-medium flex items-center gap-2">
            <LocationTargetSvg />
            {timeslot.rampName}
          </div>
        )}
        <div className="mt-1">
          {date && (
            <div className="flex items-center gap-2">
              <CalendarSvg className={'text-content-secondary'} />
              {date}
            </div>
          )}
          {hasInterval && (
            <div className="flex items-center gap-2 text-gray-600">
              <ClockSvg />
              {since} - {till}
            </div>
          )}
          <COTimezone timezoneId={timeslot.timezoneId} />
        </div>
      </div>
    );
  }

  if (isNotNullOrUndefined(supplierNotification)) {
    return (
      <div className="flex flex-col gap-4">
        {supplierNotification.supplierName && (
          <div className="text-[#616161] flex items-center gap-2">
            <BuildingSvg />
            {supplierNotification.supplierName}
          </div>
        )}
        <SupplierNotificationLocation location={supplierNotification.loadingLocation} />
        <SupplierNotificationLocation location={supplierNotification.unloadingLocation} />
      </div>
    );
  }

  return null;
};

export default NotificationAdditionalInfo;